import {
  createColumnHelper,
  type PaginationState,
  type SortingFn,
  type SortingState,
} from '@tanstack/react-table';
import { format, isValid, subMonths } from 'date-fns';
import { useMemo, useState } from 'react';
import { type DateRange } from 'react-day-picker';

import { useGetParentPaymentHistory } from '~entities/parent/api/get-parent-payment-history';
import { type PaymentHistory } from '~entities/parent/model/payment.type';
import { PaymentStatus } from '~entities/payment/constants/index';
import {
  type PaymentHistorySortOrder,
  STUDENT_PAYMENT_HISTORY_DATE_FIELD,
  type StudentPaymentHistoryDateField,
} from '~entities/student/api/get-student-payment-history';

import { TransactionsTableActions } from '~features/parents/ui/transactions/transactions-table-actions';
import { DateRangePicker } from '~features/payment/ui/date-range-picker';

import { useGetCurrency } from '~shared/api/currency-api';
import {
  EnrollioTable,
  EnrollioTableColumnToggle,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import { DataTableColumnHeader } from '~shared/components/table/header';
import { parseToNoonDate } from '~shared/helpers/datetime';
import { normalizePayments } from '~shared/helpers/normalize-payments';
import { getStoredPageSize } from '~shared/helpers/page-size-preference';
import { formatPaymentMethodSummary } from '~shared/helpers/payment-method-summary';
import { cn } from '~shared/shadcn/lib/utils';
import { Badge } from '~shared/shadcn/ui/badge';
import { Label } from '~shared/shadcn/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '~shared/shadcn/ui/tooltip';

const columnHelper = createColumnHelper<PaymentHistory>();

/** `date-fns/format` throws RangeError on invalid dates; API strings are not always parseable. */
function formatInvoiceDate(value: string | Date | null | undefined): string {
  const atNoon = parseToNoonDate(value);
  if (atNoon == null || !isValid(atNoon)) return '—';
  return format(atNoon, 'MMM d, yyyy');
}

/** Missing/unparseable dates sort as earliest, so they sink last in descending order. */
const sortNullableDate: SortingFn<PaymentHistory> = (rowA, rowB, columnId) => {
  const time = (row: typeof rowA) =>
    parseToNoonDate(row.getValue<string | null>(columnId))?.getTime() ??
    Number.NEGATIVE_INFINITY;
  return time(rowA) - time(rowB);
};

/** Amount columns arrive as strings; compare numerically, treating junk as 0. */
const sortNumericString: SortingFn<PaymentHistory> = (rowA, rowB, columnId) => {
  const amount = (row: typeof rowA) => {
    const parsed = Number(row.getValue(columnId));
    return Number.isFinite(parsed) ? parsed : 0;
  };
  return amount(rowA) - amount(rowB);
};

/** `payments` is an array; sort by the same summary the cell renders. */
const sortPaymentMethodSummary: SortingFn<PaymentHistory> = (rowA, rowB) =>
  formatPaymentMethodSummary(rowA.original.payments).localeCompare(
    formatPaymentMethodSummary(rowB.original.payments),
  );

interface TransactionsTableProps {
  parentId: string;
}

export const TransactionsTable = ({ parentId }: TransactionsTableProps) => {
  const currencyData = useGetCurrency();

  const COLUMNS = useMemo(
    () => [
      columnHelper.accessor('dueDate', {
        sortingFn: sortNullableDate,
        header: (info) => (
          <DataTableColumnHeader
            className='w-36 pl-4 text-black'
            column={info.column}
            title='Due date'
            sortOnClick
          />
        ),
        cell: (info) => (
          <div className='px-4 py-2'>{formatInvoiceDate(info.getValue())}</div>
        ),
      }),
      columnHelper.accessor('paymentDate', {
        sortingFn: sortNullableDate,
        header: (info) => (
          <DataTableColumnHeader
            className='w-36 pl-4 text-black'
            column={info.column}
            title='Payment date'
            sortOnClick
          />
        ),
        cell: (info) => (
          <div className='px-4 py-2'>{formatInvoiceDate(info.getValue())}</div>
        ),
      }),
      // columnHelper.accessor('invoiceDate', {
      //   header: (info) => (
      //     <DataTableColumnHeader
      //       className='w-36 pl-4 text-black'
      //       column={info.column}
      //       title='Date'
      //     />
      //   ),
      //   cell: (info) => (
      //     <div className='px-4 py-2'>
      //       {format(new Date(info.getValue()), 'MMM d, yyyy')}
      //     </div>
      //   ),
      // }),
      columnHelper.accessor('studentName', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Student Name'
            sortOnClick
          />
        ),
        cell: (info) => <div className='px-4 py-2'>{info.getValue()}</div>,
      }),
      columnHelper.accessor('className', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Product'
            sortOnClick
          />
        ),
        cell: (info) => <div className='px-4 py-2'>{info.getValue()}</div>,
      }),
      columnHelper.accessor('status', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Status'
            sortOnClick
          />
        ),
        cell: (info) => (
          <div className='px-4 py-2'>
            <Badge
              className={cn({
                'bg-[#D4EDDA]': info.getValue() === PaymentStatus.PAID,
                'bg-[#F8D7DA]': info.getValue() === PaymentStatus.FAILED,
                'bg-[#FFF3CD]': info.getValue() === PaymentStatus.SCHEDULED,
                'bg-[#D6D6D6]': info.getValue() === PaymentStatus.CANCELLED,
              })}
              variant={null}
            >
              {info.getValue()}
            </Badge>
          </div>
        ),
      }),
      columnHelper.accessor('payments', {
        id: 'paymentType',
        sortingFn: sortPaymentMethodSummary,
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Payment Method'
            sortOnClick
          />
        ),
        cell: (info) => (
          <div className='px-4 py-2'>
            {formatPaymentMethodSummary(info.getValue()) || '—'}
          </div>
        ),
      }),
      // "Amount" should reflect the invoice total actually owed/charged (finalAmount),
      // not the pre-tax/pre-fee baseAmount.
      columnHelper.accessor('finalAmount', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Amount'
            sortOnClick
          />
        ),
        cell: (info) => (
          <div className='px-4 py-2 font-medium'>
            {currencyData.data?.symbol}
            {info.getValue()}
          </div>
        ),
      }),
      columnHelper.accessor('discount', {
        sortingFn: sortNumericString,
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Discount'
            sortOnClick
          />
        ),
        cell: (info) => (
          <div className='text-nowrap px-4 py-2 font-medium text-green-600'>
            - {currencyData.data?.symbol}
            {info.getValue()}
          </div>
        ),
      }),
      columnHelper.accessor('paidAmount', {
        sortingFn: sortNumericString,
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Paid'
            sortOnClick
          />
        ),
        cell: (info) => (
          <div className='px-4 py-2 font-medium'>
            {currencyData.data?.symbol}
            {info.getValue()}
          </div>
        ),
      }),
      columnHelper.accessor('payments', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Transaction ID'
            disableSorting
          />
        ),
        cell: (info) => {
          const payments = normalizePayments(info.getValue());
          const paymentIntentIds = payments
            .map((payment) => payment.paymentIntentId)
            .filter(Boolean);

          return (
            <div className='px-4 py-2 font-mono text-xs text-muted-foreground'>
              <Tooltip>
                <TooltipTrigger>{paymentIntentIds[0] ?? '—'}</TooltipTrigger>
                <TooltipContent>
                  <p>{paymentIntentIds.join(', ') || '—'}</p>
                </TooltipContent>
              </Tooltip>
            </div>
          );
        },
      }),
      columnHelper.accessor('failureReason', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Failure Reason'
            sortOnClick
          />
        ),
        cell: (info) => <div className='px-4 py-2'>{info.getValue()}</div>,
      }),
      columnHelper.display({
        id: 'actions',
        header: () => <div className='pl-4 text-black'>Actions</div>,
        cell: (info) => (
          <div className='px-4 py-2'>
            <TransactionsTableActions
              key={info.row.original._id}
              invoiceId={info.row.original._id}
              parentId={parentId}
              studentId={info.row.original.studentId}
              paymentProvider={info.row.original.paymentProvider}
              status={info.row.original.status}
            />
          </div>
        ),
      }),
    ],
    [currencyData.data?.symbol, parentId],
  );

  const today = new Date();
  const threeMonthsAgo = subMonths(today, 3);

  const [date, setDate] = useState<DateRange | undefined>({
    from: threeMonthsAgo,
    to: today,
  });
  const [dateField, setDateField] = useState<StudentPaymentHistoryDateField>(
    STUDENT_PAYMENT_HISTORY_DATE_FIELD.PAID,
  );
  const [sortOrder, setSortOrder] = useState<PaymentHistorySortOrder>('desc');
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: getStoredPageSize(),
  });

  const { data, isFetching } = useGetParentPaymentHistory({
    parentId,
    startDate: date?.from ? format(new Date(date.from), 'yyyy-MM-dd') : '',
    endDate: date?.to ? format(new Date(date.to), 'yyyy-MM-dd') : '',
    dateField,
    sortOrder,
  });

  return (
    <EnrollioTable
      ariaLabel='Parent transactions'
      totalLabel='Transactions'
      filterHeader={(table) => (
        <div className='flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end'>
          <EnrollioTableSearch
            placeholder='Search students...'
            value={
              (table.getColumn('studentName')?.getFilterValue() as
                | string
                | undefined) ?? ''
            }
            onChange={(value) => {
              table.getColumn('studentName')?.setFilterValue(value);
            }}
          />
          <div className='grid gap-2'>
            <Label
              htmlFor='student-payment-history-date-field'
              className='text-muted-foreground'
            >
              Date range
            </Label>
            <DateRangePicker date={date} setDate={setDate} />
          </div>
          <div className='grid gap-2'>
            <Label
              htmlFor='student-payment-history-date-field'
              className='text-muted-foreground'
            >
              Date type
            </Label>
            <Select
              value={dateField}
              onValueChange={(value) => {
                setDateField(value as StudentPaymentHistoryDateField);
              }}
            >
              <SelectTrigger
                id='student-payment-history-date-field'
                className='w-40'
                aria-label='Date filter type'
              >
                <SelectValue placeholder='Date type' />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value={STUDENT_PAYMENT_HISTORY_DATE_FIELD.DUE}>
                    Due date
                  </SelectItem>
                  <SelectItem value={STUDENT_PAYMENT_HISTORY_DATE_FIELD.PAID}>
                    Paid date
                  </SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          <div className='grid gap-2'>
            <Label
              htmlFor='student-payment-history-sort-order'
              className='text-muted-foreground'
            >
              Sort order
            </Label>
            <Select
              value={sortOrder}
              onValueChange={(value) => {
                setSortOrder(value as PaymentHistorySortOrder);
              }}
            >
              <SelectTrigger
                id='student-payment-history-sort-order'
                className='w-36'
                aria-label='Sort order'
              >
                <SelectValue placeholder='Order' />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value='desc'>Descending</SelectItem>
                  <SelectItem value='asc'>Ascending</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
      toolbar={(table) => <EnrollioTableColumnToggle table={table} />}
      columns={COLUMNS}
      data={data ?? []}
      getRowId={(row) => row._id}
      isUpdating={isFetching}
      onPaginationChange={setPagination}
      onSortingChange={setSorting}
      pagination={pagination}
      sorting={sorting}
      autoResetPageIndex
    />
  );
};
